import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';

import { ProductService } from '../services/product.service';
import { sanitizeSlug } from '../utils/utils';
import { UtmPreservationService } from '../services/utm-preservation.service';
import { MindCatalogStore } from '../store/mind/mind-catalog.store';

@Injectable({
  providedIn: 'root'
})
export class CategoryGuard implements CanActivate {
  private productService = inject(ProductService);
  private router = inject(Router);
  private utmPreservationService = inject(UtmPreservationService);
  private mindStore = inject(MindCatalogStore);
  private platformId = inject(PLATFORM_ID);


  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    const categorySlug = route.paramMap.get('category');
    
    if (!categorySlug) {
      this.router.navigate(['/404']);
      return of(false);
    }

    return this.productService.getCategories().pipe(
      map(categoriesResponse => {
        const categories = categoriesResponse.data || [];
        const categoryExists = categories.some(category => 
          sanitizeSlug(category.name) === categorySlug
        );

        const mindCategoryExists = this.mindCategorySlugExists(categorySlug);

        if (!categoryExists && !mindCategoryExists) {
          const hasUtmSource = this.hasUtmSourceInUrl();
          
          if (hasUtmSource) {
            const currentQueryParams: Record<string, string> = {};
            if (isPlatformBrowser(this.platformId)) {
              const urlParams = new URLSearchParams(window.location.search);
              urlParams.forEach((value, key) => {
                currentQueryParams[key] = value;
              });
            } else {
              Object.assign(currentQueryParams, route.queryParams);
            }
            
            const queryParams = this.utmPreservationService.addPreservedUtmToQueryParams({ ...currentQueryParams });
            
            this.router.navigate(['/'], {
              queryParams,
              replaceUrl: true
            });
          } else {
            this.router.navigate(['/404']);
          }
          return false;
        }

        return true;
      }),
      catchError(() => {
        this.router.navigate(['/404']);
        return of(false);
      })
    );
  }

  private mindCategorySlugExists(categorySlug: string): boolean {
    const groups = this.mindStore.snapshot?.groups ?? [];
    return groups.some(
      g =>
        (g.items?.length ?? 0) > 0 &&
        sanitizeSlug(g.title) === categorySlug
    );
  }

  private hasUtmSourceInUrl(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }
    
    try {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.has('utm_source');
    } catch {
      return false;
    }
  }
}
